/**
 * AssessmentsTab — manage assessment definitions and capture scores.
 *
 * Two panes side by side: left = assessment list (CRUD), right = the active
 * assessment's scoring grid (one row per trainee with editable score input).
 * Saving a score auto-recomputes the trainee's Assessment % and alert.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, Trash2, Pencil, Save, RefreshCw, Award, XCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { TraineeFilterBar, useTraineeFilter, ALL } from './TraineeFilterContext';

const EMPTY = {
  name: '', max_marks: 100, passing_marks: 50, weightage: 1,
  quiz_id: '', description: '',
};

const ALL_BATCHES = '__all__';
const NO_QUIZ = '__none__';

export default function AssessmentsTab() {
  const { get, post, put, del } = useApi();
  const { hasRole } = useAuth();
  const canAdmin = hasRole('org_admin');
  const canScore = hasRole('trainer');
  const { batchId, departmentId, visibleBatches, batches } = useTraineeFilter();

  const [assessments, setAssessments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const [quizzes, setQuizzes] = useState([]);
  const [trainees, setTrainees] = useState([]);
  const [attempts, setAttempts] = useState([]);

  // Local edits buffer: { trainee_id: scoreString }
  const [scoreEdits, setScoreEdits] = useState({});
  const [saving, setSaving] = useState(false);

  // Definition dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get('/trainees/assessments');
      const list = res.assessments || [];
      setAssessments(list);
      // Functional update keeps this callback stable (no `selected` dep),
      // preventing the mount effect from re-firing in an infinite loop.
      setSelected(prev => {
        if (!prev) return list[0] || null;
        return list.find(a => a.assessment_id === prev.assessment_id) || prev;
      });
    } catch { toast.error('Failed to load assessments'); }
    finally { setLoading(false); }
  }, [get]);

  const loadSidecar = useCallback(async () => {
    try {
      const tRes = await get('/trainees?limit=500');
      setTrainees(tRes.trainees || []);
    } catch { /* tolerated */ }
    try {
      const qRes = await get('/quizzes');
      setQuizzes(qRes.quizzes || qRes || []);
    } catch { setQuizzes([]); }
  }, [get]);

  const loadAttempts = useCallback(async (a) => {
    if (!a) { setAttempts([]); return; }
    try {
      const res = await get(`/trainees/assessments/${a.assessment_id}/attempts`);
      setAttempts(res.attempts || []);
    } catch { setAttempts([]); }
  }, [get]);

  useEffect(() => { loadAssessments(); loadSidecar(); }, [loadAssessments, loadSidecar]);
  useEffect(() => { loadAttempts(selected); setScoreEdits({}); }, [selected, loadAttempts]);

  const attemptByTrainee = useMemo(
    () => Object.fromEntries(attempts.map(a => [a.trainee_id, a])),
    [attempts]
  );
  const filteredTrainees = useMemo(() => {
    if (batchId !== ALL) return trainees.filter(t => t.batch_id === batchId);
    if (departmentId !== ALL) {
      const deptBatchIds = new Set(visibleBatches.map(b => b.batch_id));
      return trainees.filter(t => deptBatchIds.has(t.batch_id));
    }
    return trainees;
  }, [trainees, batchId, departmentId, visibleBatches]);

  // ─── Definition CRUD ──────────────────────────────────────────────────
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenDialog(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, max_marks: a.max_marks, passing_marks: a.passing_marks,
      weightage: a.weightage, quiz_id: a.quiz_id || '',
      description: a.description || '',
    });
    setOpenDialog(true);
  };

  const onSaveDef = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (Number(form.passing_marks) > Number(form.max_marks)) {
      toast.error('Passing marks cannot exceed max marks');
      return;
    }
    const payload = {
      name: form.name.trim(),
      max_marks: Number(form.max_marks),
      passing_marks: Number(form.passing_marks),
      weightage: Number(form.weightage),
      quiz_id: form.quiz_id === NO_QUIZ || !form.quiz_id ? null : form.quiz_id,
      description: form.description || null,
    };
    try {
      let created;
      if (editing) {
        await put(`/trainees/assessments/${editing.assessment_id}`, payload);
      } else {
        created = await post('/trainees/assessments', payload);
      }
      // Close the dialog FIRST (Radix Dialog overlay otherwise intercepts
      // pointer events on the page beneath while the refetch is in flight).
      setOpenDialog(false);
      setEditing(null);
      setForm(EMPTY);
      if (created) {
        setSelected(created);
        toast.success('Assessment created — a score field has been added automatically');
      } else {
        toast.success('Assessment updated');
      }
      await loadAssessments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const onDeleteDef = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/assessments/${confirmDelete.assessment_id}`);
      toast.success('Assessment deactivated');
      if (selected?.assessment_id === confirmDelete.assessment_id) setSelected(null);
      setConfirmDelete(null);
      await loadAssessments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // ─── Scoring ──────────────────────────────────────────────────────────
  const onChangeScore = (traineeId, raw) => {
    setScoreEdits({ ...scoreEdits, [traineeId]: raw });
  };

  const onSaveScores = async () => {
    if (!selected) return;
    const entries = Object.entries(scoreEdits)
      .filter(([, v]) => v !== '' && v !== undefined && v !== null)
      .map(([trainee_id, v]) => ({ trainee_id, score: Number(v) }));
    if (entries.length === 0) { toast.error('No score changes to save'); return; }
    setSaving(true);
    try {
      const res = await post(`/trainees/assessments/${selected.assessment_id}/bulk`,
        { scores: entries });
      toast.success(`Saved ${res.saved} score${res.saved === 1 ? '' : 's'} · ${res.passed} passed`);
      setScoreEdits({});
      await Promise.all([loadAssessments(), loadAttempts(selected), loadSidecar()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSyncQuiz = async () => {
    if (!selected?.quiz_id) return;
    try {
      const res = await post(`/trainees/assessments/${selected.assessment_id}/sync-quiz`);
      toast.success(`Synced ${res.synced} score${res.synced === 1 ? '' : 's'} (${res.not_matched} unmatched)`);
      await Promise.all([loadAssessments(), loadAttempts(selected), loadSidecar()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="assessments-tab">
      {/* Left: definition list */}
      <div className="lg:col-span-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-base font-semibold">Assessments</h3>
          {canAdmin && (
            <Button size="sm" onClick={openCreate} data-testid="create-assessment-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          )}
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <Card className="divide-y" data-testid="assessment-list">
            {assessments.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">No assessments yet.</p>
            ) : assessments.map(a => (
              <button
                key={a.assessment_id}
                onClick={() => setSelected(a)}
                className={`w-full text-left p-3 hover:bg-muted/50 transition ${
                  selected?.assessment_id === a.assessment_id ? 'bg-muted' : ''
                } ${!a.is_active ? 'opacity-50' : ''}`}
                data-testid={`assessment-item-${a.assessment_id}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium">{a.name}</span>
                  {!a.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Max {a.max_marks}</span>
                  <span>·</span>
                  <span>Pass {a.passing_marks}</span>
                  <span>·</span>
                  <span>×{a.weightage}</span>
                  {a.quiz_id && (
                    <Badge variant="outline" className="text-[10px] ml-1">Quiz</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {a.attempts_count} attempts · {a.pass_count} passed
                </div>
              </button>
            ))}
          </Card>
        )}
      </div>

      {/* Right: scoring grid */}
      <div className="lg:col-span-8 space-y-3">
        {!selected ? (
          <Card className="p-12 text-center">
            <Award className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-display text-lg font-semibold">
              Select an assessment
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Pick one from the left to enter scores. Or create a new one to get started.
            </p>
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap justify-between gap-2 items-end">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold">{selected.name}</h3>
                  {canAdmin && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(selected)}
                              data-testid="edit-assessment-btn">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost"
                              onClick={() => setConfirmDelete(selected)}
                              data-testid="delete-assessment-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Field key: <code>{selected.field_key}</code>
                  {selected.description && <> · {selected.description}</>}
                </p>
              </div>
              <div className="flex gap-2 items-end">
                <TraineeFilterBar />
                {selected.quiz_id && (
                  <Button variant="outline" onClick={onSyncQuiz} data-testid="sync-quiz-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Sync from Quiz
                  </Button>
                )}
                {canScore && (
                  <Button onClick={onSaveScores} disabled={saving}
                          data-testid="save-scores-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving…' : `Save${Object.keys(scoreEdits).length ? ` (${Object.keys(scoreEdits).length})` : ''}`}
                  </Button>
                )}
              </div>
            </div>

            <Card>
              {filteredTrainees.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground" data-testid="assessments-empty-state">
                  No trainees in this batch. Add trainees first.
                </p>
              ) : (
                <Table data-testid="assessment-scoring-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trainee</TableHead>
                      <TableHead>Batch</TableHead>
                      <TableHead className="w-40">Score (max {selected.max_marks})</TableHead>
                      <TableHead className="w-24">Result</TableHead>
                      <TableHead className="w-32">Last Scored</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTrainees.map(t => {
                      const attempt = attemptByTrainee[t.trainee_id];
                      const edit = scoreEdits[t.trainee_id];
                      const displayed = edit !== undefined ? edit
                        : (attempt ? String(attempt.score) : '');
                      const batchName = batches.find(b => b.batch_id === t.batch_id)?.name || '—';
                      return (
                        <TableRow key={t.trainee_id} data-testid={`score-row-${t.trainee_id}`}>
                          <TableCell className="font-medium">
                            {t.name}
                            {t.employee_id && (
                              <span className="text-xs text-muted-foreground block">{t.employee_id}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{batchName}</TableCell>
                          <TableCell>
                            <Input type="number" min={0} max={selected.max_marks}
                                   value={displayed}
                                   disabled={!canScore}
                                   onChange={e => onChangeScore(t.trainee_id, e.target.value)}
                                   className={edit !== undefined ? 'border-amber-500' : ''}
                                   data-testid={`score-input-${t.trainee_id}`} />
                          </TableCell>
                          <TableCell>
                            {attempt ? (
                              attempt.passed ? (
                                <span className="text-emerald-700 inline-flex items-center text-xs gap-1">
                                  <CheckCircle2 className="w-4 h-4" /> Pass
                                </span>
                              ) : (
                                <span className="text-rose-700 inline-flex items-center text-xs gap-1">
                                  <XCircle className="w-4 h-4" /> Fail
                                </span>
                              )
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {attempt ? new Date(attempt.scored_at).toLocaleDateString() : '—'}
                            {attempt?.source === 'quiz_sync' && (
                              <Badge variant="outline" className="ml-1 text-[10px]">Quiz</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
            <p className="text-xs text-muted-foreground">
              Each saved score auto-updates the trainee&apos;s <code>Assessment %</code> field
              and may flip the <code>Alert Status</code> if rules trigger.
            </p>
          </>
        )}
      </div>

      {/* Definition dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg" data-testid="assessment-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Assessment' : 'New Assessment'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name}
                     onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Final Practical Exam"
                     data-testid="assessment-input-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Max marks</Label>
                <Input type="number" min={1} value={form.max_marks}
                       onChange={e => setForm({ ...form, max_marks: e.target.value })}
                       data-testid="assessment-input-max" />
              </div>
              <div>
                <Label>Passing</Label>
                <Input type="number" min={0} value={form.passing_marks}
                       onChange={e => setForm({ ...form, passing_marks: e.target.value })}
                       data-testid="assessment-input-passing" />
              </div>
              <div>
                <Label>Weight</Label>
                <Input type="number" min={0.1} step={0.1} value={form.weightage}
                       onChange={e => setForm({ ...form, weightage: e.target.value })}
                       data-testid="assessment-input-weight" />
              </div>
            </div>
            <div>
              <Label>Link to a Quiz (optional)</Label>
              <Select value={form.quiz_id || NO_QUIZ}
                      onValueChange={v => setForm({ ...form, quiz_id: v === NO_QUIZ ? '' : v })}>
                <SelectTrigger data-testid="assessment-input-quiz">
                  <SelectValue placeholder="No quiz linked" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_QUIZ}>No quiz linked</SelectItem>
                  {quizzes.map(q => (
                    <SelectItem key={q.quiz_id || q.id} value={q.quiz_id || q.id}>
                      {q.title || q.name || (q.quiz_id || q.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                If linked, you can pull scores from Lumyn quiz attempts via &quot;Sync from Quiz&quot;.
              </p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} rows={2}
                        onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={onSaveDef} data-testid="assessment-save-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmDelete?.name}</strong> will be deactivated. Existing scores
              stay on the trainee profile (so historical exports still work), but it
              will no longer contribute to the Assessment % calculation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDeleteDef} data-testid="confirm-delete-assessment-btn">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
